import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../utils/translations';

const TikTokIcon = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const linkClass = 'text-[#8a8a8a] hover:text-[#b0b0b0] text-sm transition-colors duration-300';
const iconClass = 'text-[#6a6a6a] hover:text-[#9a9a9a] transition-colors duration-300';

const Footer = () => {
  const { language } = useLanguage();

  return (
    <footer className="bg-black text-white">
      {/* Tagline */}
      <div className="border-b border-white/[0.025] py-7 text-center">
        <p className="text-xs sm:text-[13px] tracking-[0.24em] font-light" style={{ color: 'rgba(255,255,255,0.45)' }}>
          {language === 'en' ? 'Custodians, not collectors.' :
           language === 'es' ? 'Custodios, no coleccionistas.' :
           language === 'pt' ? 'Guardadores, não colecionadores.' :
           language === 'fr' ? 'Gardiens, pas collectionneurs.' :
           'Custodi, non collezionisti.'}
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4" style={{ gap: '4.5rem' }}>
          {/* Brand */}
          <div className="space-y-4">
            <span className="text-3xl font-bold tracking-wider">REVS VIBES</span>
            <p className="text-sm leading-relaxed max-w-[260px] whitespace-pre-line" style={{ color: '#8a8a8a' }}>
              {getTranslation(language, 'footer.description')}
            </p>
            <div className="flex space-x-4">
              <a href="https://www.instagram.com/revsvibes?igsh=MTI4dzVkYnV2b2Zrbw==" target="_blank" rel="noopener noreferrer" className={iconClass}>
                <Instagram size={17} />
              </a>
              <a href="https://www.facebook.com/share/1C9pZRq1GF/" target="_blank" rel="noopener noreferrer" className={iconClass}>
                <Facebook size={17} />
              </a>
              <a href="https://www.tiktok.com/@revsvibes?_r=1&_t=ZT-93iBvykKi7h" target="_blank" rel="noopener noreferrer" className={iconClass}>
                <TikTokIcon size={17} />
              </a>
              <a href="https://youtube.com/@revsvibesmedia?si=mT0XI8MIBwkPErBL" target="_blank" rel="noopener noreferrer" className={iconClass}>
                <Youtube size={17} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-[11px] font-semibold tracking-[0.1em] mb-5" style={{ color: '#6a6a6a' }}>{getTranslation(language, 'footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li><Link to="/services" className={linkClass}>{getTranslation(language, 'nav.services')}</Link></li>
              <li><Link to="/about" className={linkClass}>{getTranslation(language, 'nav.about')}</Link></li>
              <li><Link to="/blog" className={linkClass}>{getTranslation(language, 'nav.blog')}</Link></li>
              <li><Link to="/contact" className={linkClass}>{getTranslation(language, 'footer.contact')}</Link></li>
            </ul>
          </div>

          {/* Community */}
          <div>
            <h3 className="text-[11px] font-semibold tracking-[0.1em] mb-5" style={{ color: '#6a6a6a' }}>REVS COMMUNITY</h3>
            <ul className="space-y-2">
              <li><Link to="/community" className={linkClass}>{getTranslation(language, 'footer.events')}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-[11px] font-semibold tracking-[0.1em] mb-5" style={{ color: '#6a6a6a' }}>{getTranslation(language, 'footer.contact')}</h3>
            <ul className="space-y-2">
              <li className="flex items-start space-x-3 text-sm" style={{ color: '#8a8a8a' }}>
                <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                <span style={{ whiteSpace: 'pre-line' }}>{getTranslation(language, 'footer.location')}</span>
              </li>
              <li className="flex items-center space-x-3 text-sm" style={{ color: '#8a8a8a' }}>
                <Phone size={14} className="flex-shrink-0" />
                <span>+1 (407) 456-3086</span>
              </li>
              <li className="flex items-center space-x-3 text-sm" style={{ color: '#8a8a8a' }}>
                <Mail size={14} className="flex-shrink-0" />
                <span>info@revsvibes.com</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 pt-7 border-t border-white/[0.03]">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm" style={{ color: '#505050' }}>
              &copy; 2026 Revs Vibes. {getTranslation(language, 'footer.rights')}.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/privacy" className="text-sm transition-colors duration-300" style={{ color: '#505050' }}>
                {getTranslation(language, 'footer.privacy')}
              </Link>
              <Link to="/terms" className="text-sm transition-colors duration-300" style={{ color: '#505050' }}>
                {getTranslation(language, 'footer.terms')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
